import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SendIcon from '@mui/icons-material/Send'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextareaAutosize,
  Tooltip,
  Typography,
} from '@mui/material'
import { useChat } from '@ai-sdk/react'
import { useQuery } from '@tanstack/react-query'
import type { Message } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useSelector } from 'react-redux'
import interfaceApi from '@/api/admin/interfaceApi'
import { useAuthContext } from '@/features/auth/context'
import {
  encodeConfirmationPayload,
  parseConfirmedSignature,
} from '@/features/copilot/confirmationPayload'
import ConversationDrawer from '@/features/copilot/ConversationDrawer'
import {
  getConversation,
  listConversations,
} from '@/features/copilot/copilotConversationStore'
import MessageBubble from '@/features/copilot/MessageBubble'
import {
  COPILOT_CHAT_ENDPOINT,
  COPILOT_SIZE_BOUNDS,
  CopilotCorner,
  getCopilotMaxHeight,
  getCopilotMaxWidth,
} from '@/features/copilot/constants'
import type { ProposedAction } from '@/features/copilot/ProposedActionCard'
import { useCopilotConversations } from '@/features/copilot/useCopilotConversations'
import useTranslation from '@/hooks/useTranslation'
import { selectUser } from '@/store/selectors/UserSelector'
import { getStoredToken } from '@/utils/jwtUtils'

const COPILOT_SUGGESTIONS = [
  {
    id: 'action',
    labelKey: 'CopilotCategoryAction',
    descKey: 'CopilotCategoryActionDesc',
    exampleKey: 'CopilotExampleAction',
  },
  {
    id: 'deploy',
    labelKey: 'CopilotCategoryDeploy',
    descKey: 'CopilotCategoryDeployDesc',
    exampleKey: 'CopilotExampleDeploy',
  },
  {
    id: 'analysis',
    labelKey: 'CopilotCategoryAnalysis',
    descKey: 'CopilotCategoryAnalysisDesc',
    exampleKey: 'CopilotExampleAnalysis',
  },
] as const

interface CopilotPanelProps {
  onClose: () => void
  onOpenSettings: () => void
  corner: CopilotCorner
  width: number
  height: number
  offsetX: number
  offsetY: number
  expanded?: boolean
  onToggleExpand?: () => void
  onCollapse?: () => void
  onResize: (size: { width: number; height: number }) => void
  onResetSize?: () => void
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const buildMessagesSignature = (messages: Message[]): string => {
  if (messages.length === 0) return '0::0'
  const last = messages[messages.length - 1]
  const lastContentLen =
    typeof last?.content === 'string' ? last.content.length : 0
  return `${messages.length}:${last?.id ?? ''}:${lastContentLen}`
}

const mapCopilotErrorMessage = (raw: string): string | null => {
  if (!raw) return null
  let code: string | null = null
  try {
    const parsed = JSON.parse(raw) as { error?: unknown }
    if (typeof parsed?.error === 'string') code = parsed.error
  } catch {
    // not JSON — fall through to substring matching
  }
  if (code === 'daily_token_budget_exceeded') {
    return 'Bạn đã đạt giới hạn token hôm nay. Vui lòng quay lại sau 24h.'
  }
  if (code === 'rate_limit_exceeded') {
    return 'Quá nhiều yêu cầu. Vui lòng chờ vài phút.'
  }
  if (code === 'unauthorized') {
    return 'Phiên đăng nhập đã hết. Vui lòng tải lại trang.'
  }
  const lower = raw.toLowerCase()
  if (
    lower.includes('conversation_too_long') ||
    lower.includes('entity too large') ||
    lower.includes('413')
  ) {
    return 'Hội thoại quá dài. Bấm New chat để bắt đầu lại.'
  }
  if (
    /model tried to call unavailable tool|unavailable tool|tool .* not found/i.test(
      raw,
    )
  ) {
    return 'Câu hỏi này tôi chưa xử lý được. Bạn vui lòng hỏi theo cách khác giúp tôi.'
  }
  if (
    /\b5\d\d\b/.test(raw) ||
    /internal server error|bad gateway|service unavailable|gateway timeout/i.test(
      raw,
    )
  ) {
    return 'Hệ thống đang gặp sự cố. Vui lòng thử lại sau.'
  }
  return null
}

const formatConversationForCopy = (
  messages: Message[],
  meta: { interfaceId: number | null; interfaceName: string | null },
): string => {
  const header: string[] = ['CisgenX Copilot Conversation']
  if (meta.interfaceId !== null) {
    header.push(
      meta.interfaceName
        ? `Interface #${meta.interfaceId} - ${meta.interfaceName}`
        : `Interface #${meta.interfaceId}`,
    )
  }
  header.push(`Exported: ${new Date().toISOString()}`)
  const lines: string[] = [header.join('\n'), '---', '']

  for (const message of messages) {
    if (message.role !== 'user' && message.role !== 'assistant') continue
    const text =
      typeof message.content === 'string' ? message.content.trim() : ''

    if (message.role === 'user') {
      // CONFIRM_TOOL follow-ups are machine payloads, not user-authored text
      if (text.startsWith('CONFIRM_TOOL')) continue
      if (!text) continue
      lines.push('User:', text, '')
      continue
    }

    const blocks: string[] = []
    if (text) blocks.push(text)
    const invocations = message.toolInvocations ?? []
    for (const inv of invocations) {
      if (inv.state !== 'result') continue
      const result = (inv as { result?: unknown }).result as
        | { type?: string; action?: string; summary?: string }
        | null
      if (result?.type === 'proposedAction') {
        const label =
          result.summary ?? `Proposed action: ${result.action ?? 'unknown'}`
        blocks.push(`[${label}]`)
      }
    }
    if (!blocks.length) continue
    lines.push('Copilot:', blocks.join('\n\n'), '')
  }

  return lines.join('\n').trimEnd() + '\n'
}

const CopilotPanel = ({
  onClose,
  onOpenSettings,
  corner,
  width,
  height,
  offsetX,
  offsetY,
  expanded = false,
  onToggleExpand,
  onCollapse,
  onResize,
  onResetSize,
}: CopilotPanelProps) => {
  const currentUser = useSelector(selectUser)
  const copilotUserId = currentUser?.id ?? null
  const {
    conversations,
    currentId,
    current: currentConversation,
    isReady: isConversationsReady,
    remoteSyncCount,
    newChat: convNewChat,
    selectChat: convSelectChat,
    deleteChat: convDeleteChat,
    renameChat: convRenameChat,
    saveMessages: convSaveMessages,
  } = useCopilotConversations({ userId: copilotUserId })
  const { interfaceId, setInterfaceId, logout } = useAuthContext()
  const { t } = useTranslation()
  const [lastInterfaceName, setLastInterfaceName] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const lastSavedSigRef = useRef<string>('0::0')
  const seededConvIdRef = useRef<string | null | undefined>(undefined)
  const wasLoadingRef = useRef<boolean>(false)
  const lastAppliedRemoteSyncRef = useRef<number>(0)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // Pass the logged-in user's legacy JWT so the copilot backend can identify
  // the caller. Without this header the BE falls back to a shared SuperAdmin
  // identity (configured server-side) and every user appears as SuperAdmin.
  const authToken = getStoredToken()
  const authHeaders: Record<string, string> = authToken
    ? { Authorization: `Bearer ${authToken}` }
    : {}

  const { data: interfaceData } = useQuery({
    queryKey: ['copilot-interface', interfaceId],
    queryFn: () => interfaceApi.getById(String(interfaceId)),
    enabled: !!interfaceId,
    staleTime: 5 * 60 * 1000,
  })
  const interfaceDetail = interfaceData?.data as
    | {
        customName?: string
        name?: string
        countryName?: string
        timeZoneName?: string
        timeZoneOriginId?: string
      }
    | undefined
  const fetchedName =
    interfaceDetail?.customName || interfaceDetail?.name || null
  const displayName = lastInterfaceName ?? fetchedName
  const timezoneLine = [
    interfaceDetail?.countryName,
    interfaceDetail?.timeZoneName,
    interfaceDetail?.timeZoneOriginId,
  ]
    .filter(Boolean)
    .join(' · ')

  // Ref-based handler so the `useChat` onError closure (created once on
  // hook init) can always reach the latest logout/close logic without
  // re-initialising the chat hook on every render.
  const authExpiredHandledRef = useRef(false)
  const authExpiredHandlerRef = useRef<() => void>(() => undefined)

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    stop,
    setInput,
    setMessages,
    append,
    data,
  } = useChat({
    api: COPILOT_CHAT_ENDPOINT,
    headers: authHeaders,
    body: {
      context: {
        interfaceId: interfaceId ?? null,
        interfaceName: displayName,
      },
    },
    async onToolCall({ toolCall }) {
      if (toolCall.toolName === 'selectInterface') {
        const args = toolCall.args as {
          interfaceId: number
          interfaceName: string
        }
        setInterfaceId(args.interfaceId)
        setLastInterfaceName(args.interfaceName)
        return `Selected interface: ${args.interfaceName} (id=${args.interfaceId})`
      }
      return undefined
    },
    onError(err) {
      // Log raw payload before any mapping so devtools always show the
      // unmodified BE response when triaging copilot errors.
      console.error('Copilot chat error:', err)
      // The BE's pipeDataStreamToResponse onError maps AuthExpiredError to
      // the literal string 'auth_expired'. This is the fallback path when
      // the streamed data-part signal (watched below) does not land because
      // the stream failed before `writer.writeData` ran.
      if (err?.message === 'auth_expired') {
        authExpiredHandlerRef.current()
        return
      }
      const msg = err?.message?.toLowerCase() || ''
      if (
        msg.includes('conversation_too_long') ||
        msg.includes('entity too large') ||
        msg.includes('413')
      ) {
        toast.error('Hội thoại quá dài. Bấm New chat để bắt đầu lại.', {
          duration: 8000,
        })
      }
    },
  })

  const handleAuthExpired = useCallback(() => {
    if (authExpiredHandledRef.current) return
    authExpiredHandledRef.current = true
    // Stop any in-flight stream before tearing down auth state so the SDK
    // does not race against the logout-driven unmount.
    try {
      stop()
    } catch (stopErr) {
      console.error('Failed to stop copilot stream on auth expiry:', stopErr)
    }
    // Reset the active conversation so the next session starts fresh. We keep
    // stored conversations in IndexedDB because the user may want to revisit
    // them after re-logging in on the same browser profile.
    convNewChat()
    setMessages([])
    lastSavedSigRef.current = '0::0'
    toast.error('Session expired, please log in again')
    // logout() from AuthProvider: removes token, clears localStorage keys,
    // resets Redux user, clears React Query cache, navigates to '/'.
    logout()
    onClose()
  }, [convNewChat, logout, onClose, setMessages, stop])

  const handleSuggestionClick = useCallback(
    (prompt: string) => {
      setInput(prompt)
      textareaRef.current?.focus()
    },
    [setInput],
  )

  // Keep the ref pointing at the latest memoised callback so the
  // once-created onError closure above still sees up-to-date logic.
  useEffect(() => {
    authExpiredHandlerRef.current = handleAuthExpired
  }, [handleAuthExpired])

  // Primary signal: BE emits `{type:'auth_expired'}` via writer.writeData()
  // when a tool call throws AuthExpiredError. It arrives in useChat's `data`
  // array (server-appended JSON parts).
  useEffect(() => {
    if (!data || data.length === 0) return
    for (const part of data) {
      if (
        part &&
        typeof part === 'object' &&
        (part as { type?: unknown }).type === 'auth_expired'
      ) {
        handleAuthExpired()
        break
      }
    }
  }, [data, handleAuthExpired])

  // Escape while expanded collapses back to the normal (docked) size rather
  // than closing the widget entirely. When not expanded, defer to the
  // browser's / app's default handling.
  useEffect(() => {
    if (!expanded) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onCollapse?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [expanded, onCollapse])

  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Live size during drag — falls back to props when not resizing.
  const [dragSize, setDragSize] = useState<{
    width: number
    height: number
  } | null>(null)
  const effectiveWidth = dragSize?.width ?? width
  const effectiveHeight = dragSize?.height ?? height

  const isAnchorRight = corner.endsWith('-right')
  const isAnchorBottom = corner.startsWith('bottom-')
  const handleCursor =
    corner === 'top-left' || corner === 'bottom-right'
      ? 'nwse-resize'
      : 'nesw-resize'

  const handleResizeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      const startX = event.clientX
      const startY = event.clientY
      // When fullscreen, exit it and start the drag from the on-screen
      // fullscreen footprint so the panel does not snap back to the saved
      // (smaller) size before the user has moved the mouse.
      let startWidth = effectiveWidth
      let startHeight = effectiveHeight
      if (expanded) {
        startWidth = getCopilotMaxWidth(offsetX)
        startHeight = getCopilotMaxHeight(offsetY)
        setDragSize({ width: startWidth, height: startHeight })
        onCollapse?.()
      }
      const xMul = isAnchorRight ? -1 : 1
      const yMul = isAnchorBottom ? -1 : 1

      const onMove = (e: MouseEvent) => {
        const dx = (e.clientX - startX) * xMul
        const dy = (e.clientY - startY) * yMul
        setDragSize({
          width: clamp(
            startWidth + dx,
            COPILOT_SIZE_BOUNDS.minWidth,
            getCopilotMaxWidth(offsetX),
          ),
          height: clamp(
            startHeight + dy,
            COPILOT_SIZE_BOUNDS.minHeight,
            getCopilotMaxHeight(offsetY),
          ),
        })
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        setDragSize((current) => {
          if (current) onResize(current)
          return null
        })
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [
      effectiveWidth,
      effectiveHeight,
      isAnchorRight,
      isAnchorBottom,
      expanded,
      offsetX,
      offsetY,
      onCollapse,
      onResize,
    ],
  )

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, isLoading])

  // Seed-once-per-conversation: hydrate useChat from IDB exactly once for each
  // distinct conv.id (or null). Same-id content updates (e.g. our own save
  // bumping updatedAt) are skipped here — they go through the cross-tab effect
  // below if they originated from another tab.
  useEffect(() => {
    if (!isConversationsReady) return
    const targetId = currentConversation?.id ?? null
    if (seededConvIdRef.current === targetId) return
    if (isLoading) return
    seededConvIdRef.current = targetId
    const target = currentConversation?.messages ?? []
    lastSavedSigRef.current = buildMessagesSignature(target)
    lastAppliedRemoteSyncRef.current = remoteSyncCount
    setMessages(target)
  }, [
    isConversationsReady,
    currentConversation,
    isLoading,
    remoteSyncCount,
    setMessages,
  ])

  // Autosave on the isLoading transition true -> false. We persist exactly
  // once per stream completion so we never race against the seed effect or
  // the live useChat messages buffer.
  useEffect(() => {
    if (!isConversationsReady) {
      wasLoadingRef.current = isLoading
      return
    }
    if (wasLoadingRef.current && !isLoading) {
      const sig = buildMessagesSignature(messages)
      if (sig !== lastSavedSigRef.current) {
        const snapshot = messages
        lastSavedSigRef.current = sig
        ;(async () => {
          try {
            await convSaveMessages(snapshot, {
              interfaceId: interfaceId ?? null,
              interfaceName: displayName ?? null,
            })
          } catch (err) {
            console.error('Failed to persist copilot conversation:', err)
          }
        })()
      }
    }
    wasLoadingRef.current = isLoading
  }, [
    isLoading,
    isConversationsReady,
    messages,
    convSaveMessages,
    interfaceId,
    displayName,
  ])

  // Cross-tab refresh: when another tab modifies the active conversation, the
  // hook bumps remoteSyncCount. Apply that update only when we're idle so we
  // never clobber an in-flight stream from this tab.
  useEffect(() => {
    if (!isConversationsReady) return
    if (remoteSyncCount === lastAppliedRemoteSyncRef.current) return
    if (isLoading) return
    lastAppliedRemoteSyncRef.current = remoteSyncCount
    const target = currentConversation?.messages ?? []
    lastSavedSigRef.current = buildMessagesSignature(target)
    setMessages(target)
  }, [
    isConversationsReady,
    isLoading,
    remoteSyncCount,
    currentConversation,
    setMessages,
  ])

  const handleNewChat = useCallback(() => {
    try {
      stop()
    } catch (err) {
      console.error('Failed to stop copilot stream on new chat:', err)
    }
    convNewChat()
    lastSavedSigRef.current = '0::0'
    setMessages([])
  }, [convNewChat, setMessages, stop])

  const handleSelectChat = useCallback(
    async (id: string) => {
      if (id === currentId) {
        setIsDrawerOpen(false)
        return
      }
      try {
        stop()
      } catch (err) {
        console.error('Failed to stop copilot stream on chat switch:', err)
      }
      await convSelectChat(id)
      const target = await getConversation(id, copilotUserId)
      lastSavedSigRef.current = buildMessagesSignature(target?.messages ?? [])
      setMessages(target?.messages ?? [])
      setIsDrawerOpen(false)
    },
    [convSelectChat, copilotUserId, currentId, setMessages, stop],
  )

  const handleDeleteChat = useCallback(
    async (id: string) => {
      const wasCurrent = id === currentId
      if (wasCurrent) {
        try {
          stop()
        } catch (err) {
          console.error('Failed to stop copilot stream on chat delete:', err)
        }
      }
      await convDeleteChat(id)
      if (!wasCurrent) return
      const remaining = await listConversations(copilotUserId)
      const fallbackId = remaining[0]?.id ?? null
      if (fallbackId) {
        const target = await getConversation(fallbackId, copilotUserId)
        lastSavedSigRef.current = buildMessagesSignature(target?.messages ?? [])
        setMessages(target?.messages ?? [])
      } else {
        lastSavedSigRef.current = '0::0'
        setMessages([])
      }
    },
    [convDeleteChat, copilotUserId, currentId, setMessages, stop],
  )

  const handleRenameChat = useCallback(
    (id: string, title: string) => {
      convRenameChat(id, title).catch((err) =>
        console.error('Failed to rename copilot conversation:', err),
      )
    },
    [convRenameChat],
  )

  const handleClear = handleNewChat

  const handleConfirmAction = useCallback(
    (action: ProposedAction) => {
      const payload = encodeConfirmationPayload(action.args)
      if (!payload) {
        console.error('Cannot send confirmation: empty payload')
        return
      }
      // Format: CONFIRM_TOOL <toolName> <base64 JSON args> [<hmacToken>]
      // Detected by confirm.ts extractConfirmedArgs(). The base64 args are
      // matched against the args the LLM re-invokes the tool with, so a
      // confirmation for action A cannot authorise a different action B.
      // The optional 4th field is the HMAC token the server stamped on this
      // proposedAction (action.confirmToken). When the server runs with a
      // secret (prod), it rejects any marker whose token is missing or does
      // not verify — so we MUST echo it back verbatim. In dev/eval the token
      // is absent and the marker stays 3 fields (binding disabled server-side).
      const content = action.confirmToken
        ? `CONFIRM_TOOL ${action.action} ${payload} ${action.confirmToken}`
        : `CONFIRM_TOOL ${action.action} ${payload}`
      append({ role: 'user', content }).catch((err) => {
        console.error('Failed to send confirmation message:', err)
      })
    },
    [append],
  )

  // Recommendation cards (F2 analyzeIrrigationEfficiency / F4
  // recommendWeatherAdjustment) surface a `proposedUpdate` HINT, not a
  // server-signed proposedAction. The BE contract is explicit that the model
  // forwards the hint to updateProgram (re-fetching the full program first),
  // and updateProgram then runs the real CONFIRM_TOOL gate and emits a signed
  // proposedAction the user confirms via ProposedActionCard. So "Apply" must
  // NOT short-circuit into handleConfirmAction with the partial hint args
  // (that would mismatch updateProgram's full payload and be rejected) — it
  // sends a natural-language instruction so the agent drives the proper flow.
  const handleApplyRecommendation = useCallback(
    (instruction: string) => {
      if (!instruction.trim()) return
      append({ role: 'user', content: instruction }).catch((err) => {
        console.error('Failed to send apply-recommendation message:', err)
      })
    },
    [append],
  )

  const handleCopyConversation = useCallback(async () => {
    if (messages.length === 0) return
    const text = formatConversationForCopy(messages, {
      interfaceId: interfaceId ?? null,
      interfaceName: displayName,
    })
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Conversation copied to clipboard')
    } catch (err) {
      console.error('Failed to copy conversation to clipboard:', err)
      toast.error('Copy failed')
    }
  }, [messages, interfaceId, displayName])

  // Signatures of every proposedAction the user already confirmed in this
  // conversation. Used to suppress the Confirm button on duplicate widgets
  // the LLM re-emits after a successful tool execution.
  const confirmedSignatures = useMemo(() => {
    const set = new Set<string>()
    for (const msg of messages) {
      if (msg.role !== 'user') continue
      const text = typeof msg.content === 'string' ? msg.content : ''
      const sig = parseConfirmedSignature(text)
      if (sig) set.add(sig)
    }
    return set
  }, [messages])

  return (
    <Paper
      elevation={6}
      sx={{
        width: expanded ? '100%' : effectiveWidth,
        height: expanded ? '100%' : effectiveHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'border.main',
        position: 'relative',
      }}
    >
      <Box
        role="separator"
        aria-label="Resize chat panel"
        onMouseDown={handleResizeStart}
        sx={{
          position: 'absolute',
          width: 20,
          height: 20,
          zIndex: 2,
          cursor: handleCursor,
          top: isAnchorBottom ? 0 : 'auto',
          bottom: isAnchorBottom ? 'auto' : 0,
          left: isAnchorRight ? 0 : 'auto',
          right: isAnchorRight ? 'auto' : 0,
          opacity: 0.45,
          transition: 'opacity 150ms ease',
          '&:hover': { opacity: 1 },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: isAnchorBottom ? 5 : 'auto',
            bottom: isAnchorBottom ? 'auto' : 5,
            left: isAnchorRight ? 5 : 'auto',
            right: isAnchorRight ? 'auto' : 5,
            width: 9,
            height: 9,
            borderStyle: 'solid',
            borderColor: 'grey.800',
            borderWidth: 0,
            borderTopWidth: isAnchorBottom ? 2 : 0,
            borderBottomWidth: isAnchorBottom ? 0 : 2,
            borderLeftWidth: isAnchorRight ? 2 : 0,
            borderRightWidth: isAnchorRight ? 0 : 2,
          },
        }}
      />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'border.devider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ color: 'text.main', fontWeight: 600, lineHeight: 1.2 }}>
            CisgenX Copilot
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: interfaceId ? 'text.main' : 'grey.600',
              fontSize: 11,
              lineHeight: 1.2,
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {interfaceId
              ? displayName
                ? `Interface #${interfaceId} - ${displayName}`
                : `Interface #${interfaceId}`
              : 'No interface selected'}
          </Typography>
          {interfaceId && timezoneLine && (
            <Typography
              variant="caption"
              sx={{
                color: 'grey.600',
                fontSize: 10.5,
                lineHeight: 1.2,
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {timezoneLine}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            flexWrap: 'nowrap',
          }}
        >
          <Tooltip
            title={isDrawerOpen ? 'Hide conversations' : 'Conversations'}
          >
            <IconButton
              size="small"
              onClick={() => setIsDrawerOpen((v) => !v)}
              aria-label="Toggle conversations sidebar"
            >
              <ChatBubbleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New chat">
            <span>
              <IconButton
                size="small"
                onClick={handleClear}
                disabled={isLoading}
                aria-label="Start a new conversation"
              >
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Copy conversation">
            <span>
              <IconButton
                size="small"
                onClick={handleCopyConversation}
                disabled={messages.length === 0}
                aria-label="Copy conversation to clipboard"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Widget position">
            <IconButton size="small" onClick={onOpenSettings}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onResetSize && (
            <Tooltip title="Reset size">
              <IconButton
                size="small"
                onClick={onResetSize}
                aria-label="Reset widget size"
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onToggleExpand && (
            <Tooltip title={expanded ? 'Exit full screen' : 'Full screen'}>
              <IconButton
                size="small"
                onClick={onToggleExpand}
                aria-label={
                  expanded ? 'Exit full screen' : 'Expand to full screen'
                }
              >
                {expanded ? (
                  <CloseFullscreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <ConversationDrawer
        open={isDrawerOpen}
        conversations={conversations}
        currentId={currentId}
        onClose={() => setIsDrawerOpen(false)}
        onNewChat={() => {
          handleNewChat()
          setIsDrawerOpen(false)
        }}
        onSelect={handleSelectChat}
        onRename={handleRenameChat}
        onDelete={handleDeleteChat}
      />

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.length === 0 && (
          <Stack spacing={2} sx={{ mt: 3, px: 0.5 }}>
            <Typography
              sx={{
                color: 'text.main',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              {t('CopilotWelcomeTitle')}
            </Typography>
            <Stack spacing={1}>
              {COPILOT_SUGGESTIONS.map((suggestion) => (
                <Box
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(t(suggestion.exampleKey))}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'border.main',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    transition: 'border-color 150ms, background-color 150ms',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'background.overrideDefault',
                    },
                  }}
                >
                  <Typography
                    sx={{ color: 'text.main', fontWeight: 600, fontSize: 13 }}
                  >
                    {t(suggestion.labelKey)}
                  </Typography>
                  <Typography
                    sx={{ color: 'grey.600', fontSize: 12, mt: 0.25 }}
                  >
                    {t(suggestion.descKey)}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'grey.800',
                      fontSize: 11,
                      mt: 0.5,
                      fontStyle: 'italic',
                    }}
                  >
                    {`“${t(suggestion.exampleKey)}”`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        )}

        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onConfirmAction={handleConfirmAction}
            onApplyRecommendation={handleApplyRecommendation}
            disableActions={isLoading}
            confirmedSignatures={confirmedSignatures}
          />
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
              Thinking…
            </Typography>
          </Box>
        )}

        {error && error.message !== 'auth_expired' && (
          <Alert severity="error" variant="outlined" sx={{ fontSize: 13 }}>
            {mapCopilotErrorMessage(error.message) ??
              'Hệ thống đang gặp sự cố. Vui lòng thử lại sau.'}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'border.devider',
          flexShrink: 0,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: 'relative',
            pl: 1.5,
            pr: 5,
            py: 0.75,
            border: '1px solid',
            borderColor: 'border.main',
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            transition: 'border-color 150ms',
            '&:focus-within': { borderColor: 'primary.main' },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 8,
              bottom: 8,
              right: 40,
              width: '1px',
              bgcolor: 'border.devider',
              pointerEvents: 'none',
            },
            '& textarea': {
              display: 'block',
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 13.5,
              lineHeight: 1.5,
              padding: '0 12px 0 0',
              color: 'text.main',
              '&::placeholder': {
                color: 'grey.600',
                opacity: 1,
              },
              '&:disabled': {
                color: 'text.main',
                opacity: 0.6,
              },
            },
          }}
        >
          <TextareaAutosize
            ref={textareaRef}
            minRows={1}
            maxRows={8}
            placeholder="Ask about irrigation…"
            value={input}
            onChange={handleInputChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                if (!isLoading && input.trim()) {
                  handleSubmit(event as unknown as React.FormEvent<HTMLFormElement>);
                }
              }
            }}
            disabled={isLoading}
            autoComplete="off"
          />
          {isLoading ? (
            <IconButton
              color="warning"
              onClick={stop}
              type="button"
              size="small"
              sx={{ position: 'absolute', bottom: 4, right: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              type="submit"
              disabled={!input.trim()}
              size="small"
              sx={{ position: 'absolute', bottom: 4, right: 4 }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

export default CopilotPanel
